import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

const TRACKING_SESSION_KEY = 'astro_satvik_session_id';

export const useVisitorTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const initSession = async () => {
      try {
        let sessionId = localStorage.getItem(TRACKING_SESSION_KEY);
        
        if (!sessionId) {
          // Instantly generate and persist the session ID to prevent React StrictMode 
          // or rapid double-renders from creating multiple duplicate sessions in parallel.
          const newSessionId = crypto.randomUUID();
          localStorage.setItem(TRACKING_SESSION_KEY, newSessionId);
          
          // Fetch IP
          let ip_address = 'unknown';
          try {
            const res = await fetch('https://1.1.1.1/cdn-cgi/trace');
            const text = await res.text();
            const ipMatch = text.match(/ip=([^\s]+)/);
            if (ipMatch) {
              ip_address = ipMatch[1];
            }
          } catch (e) {
            // Silently ignore if blocked by browser tracking protection
          }

          const { error } = await supabase
            .from('visitor_sessions')
            .insert({
              id: newSessionId,
              ip_address,
              user_agent: navigator.userAgent,
              location: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            
          if (error) {
            // If the insert failed for some reason, we could optionally remove the token,
            // but keeping it is usually safer to avoid infinite retry loops on failure.
            console.error('Failed to insert tracking session', error);
          }
        }
      } catch (error) {
        console.error('Failed to initialize session', error);
      }
    };
    
    initSession();
  }, []);

  useEffect(() => {
    const logPageView = async () => {
      const sessionId = localStorage.getItem(TRACKING_SESSION_KEY);
      if (!sessionId) return;

      try {
        await supabase.from('visitor_events').insert({
          session_id: sessionId,
          event_type: 'page_view',
          path: location.pathname + location.search,
        });
      } catch (error) {
        console.error('Failed to log page view', error);
      }
    };

    setTimeout(logPageView, 1500);

    // Global mass tracking for clicks
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // We traverse up to find if a button, link, or menu/select option was clicked
      const clickable = target.closest('button, a, [role="button"], [role="option"], [role="menuitem"]');
      
      if (clickable) {
        // Collect identifying info without blocking
        let text = clickable.textContent || '';
        // Clean up text (remove newlines, collapse spaces)
        text = text.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100);
        
        const id = clickable.id || '';
        const tag = clickable.tagName;
        
        // Asynchronously fire-and-forget the tracking event
        logVisitorEvent('button_click', {
          text,
          id,
          tag,
          path: window.location.pathname
        });
      }
    };

    document.addEventListener('click', handleGlobalClick, { capture: true, passive: true });
    
    return () => {
      document.removeEventListener('click', handleGlobalClick, { capture: true });
    };
  }, [location.pathname, location.search]);
};

export const linkVisitorIdentifier = async (identifier: string) => {
  const sessionId = localStorage.getItem(TRACKING_SESSION_KEY);
  if (!sessionId) return;
  
  try {
    await supabase.rpc('link_visitor_identifier', { 
      p_session_id: sessionId, 
      p_identifier: identifier 
    });
  } catch (error) {
    console.error('Failed to link identifier', error);
  }
};

export const logVisitorEvent = async (eventType: string, eventData?: any) => {
  const sessionId = localStorage.getItem(TRACKING_SESSION_KEY);
  if (!sessionId) return;
  
  try {
    await supabase.from('visitor_events').insert({
      session_id: sessionId,
      event_type: eventType,
      path: window.location.pathname + window.location.search,
      event_data: eventData,
    });
  } catch (error) {
    console.error('Failed to log event', error);
  }
};
